import React, { useCallback, useEffect, useState } from "react";
import { PageLoading } from "../../../common/components/Loading";
import { useDataRequest } from "../../../common/hooks/api";
import { NetworkRequest } from "../../../common/models";
import { Api } from "../../../common/service/Api";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { Autocomplete, Button, ExplanatoryCard } from "@truevault/aquilo";
import { StepNumberCircle } from "./WorkflowSetupTabs";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { WorkflowSetupPageWrapper } from "./WorkflowSetupPageWrapper";
import { Routes } from "../../root/routes";
import { useHistory } from "react-router";
import { useIntegrationsSettings } from "../../components/superorg/settings/IntegrationsSettings";
import { BoltIcon } from "@heroicons/react/20/solid";
import { AutocompleteItem } from "@truevault/aquilo/src/components/textInput/Autocomplete";

export const CGInstructionsPage = () => {
  const history = useHistory();

  // State
  const [readyToRender, setReadyToRender] = useState(false);
  const [instructions, setInstructions] = useState("");

  // Requests
  const [organization, orgRequest, refreshOrg] = usePrimaryOrganization();
  const [allVendors, ,] = useOrganizationVendors(
    organization.id,
    // All following args are default values from function signature, except for onlyUseSettled
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ false,
  );

  const [primaryLookupSystemVendorId, setPrimaryLookupSystemVendorId] = useState<string | null>(
    null,
  );
  const [secondaryLookupSystemVendorId, setSecondaryLookupSystemVendorId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (organization) {
      setPrimaryLookupSystemVendorId(organization.primaryLookupSystemVendorId);
      setSecondaryLookupSystemVendorId(organization.secondaryLookupSystemVendorId);
    }
  }, [organization]);

  const fetchedInstructionsApi = useCallback(
    () => Api.requestHandlingInstructions.getCGInstructions(organization.id),
    [organization.id],
  );
  const { result: fetchedInstructions, request: instructionsRequest } = useDataRequest({
    queryKey: ["instructions", organization.id],
    api: fetchedInstructionsApi,
  });

  // Effects
  useEffect(() => {
    if (NetworkRequest.areFinished(orgRequest)) {
      setReadyToRender(true);
    }
  }, [orgRequest]);

  useEffect(() => {
    if (instructionsRequest.success) {
      setInstructions(fetchedInstructions);
    }
  }, [fetchedInstructions, instructionsRequest]);

  const { settings: apiIntegrations } = useIntegrationsSettings();

  if (!readyToRender || !organization || allVendors === undefined) {
    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  let primaryLookupVendor =
    primaryLookupSystemVendorId?.length > 0 &&
    allVendors.find((v) => v.vendorId === primaryLookupSystemVendorId);
  let secondaryLookupVendor =
    secondaryLookupSystemVendorId?.length > 0 &&
    allVendors.find((v) => v.vendorId === secondaryLookupSystemVendorId);

  const isPrimaryVendorAutomationConnected = apiIntegrations?.vendors?.some(
    (automationVendor) =>
      automationVendor.vendorId === primaryLookupSystemVendorId &&
      automationVendor.connected === true &&
      automationVendor.lookupEnabled === true,
  );
  const isSecondaryVendorAutomationConnected = apiIntegrations?.vendors?.some(
    (automationVendor) =>
      automationVendor.vendorId === secondaryLookupSystemVendorId &&
      automationVendor.connected === true &&
      automationVendor.lookupEnabled === true,
  );

  const atLeastOneAutomationAvailable = apiIntegrations?.vendors?.some(
    (automationVendor) =>
      automationVendor.connected === true && automationVendor.lookupEnabled === true,
  );

  return (
    <WorkflowSetupPageWrapper
      selectedTabKey="consumer-lookup"
      nextOnClick={async () => {
        history.push(Routes.organization.requestHandling.requestToDelete);
      }}
      nextDisabled={false}
    >
      <>
        <div className="tw-boundary">
          <div className="max-w-[924px]">
            <ExplanatoryCard
              topBody={
                <div>
                  <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                    Set up your consumer lookup settings
                  </h3>
                  <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                    <p>
                      When you receive a new privacy request, your first step will be to check your
                      systems in order to determine whether you store any personal data about the
                      requesting consumer.
                    </p>
                    <p>
                      Businesses typically have a "system of record" — one or two data systems where
                      they can look to see if they hold any data about someone. We recommend adding
                      your main system of record as your first lookup system below. A second system
                      of record may be needed in order to account for people who are on a marketing
                      list but have never purchased a product.
                    </p>
                  </div>
                </div>
              }
              expandable={false}
            />

            <div className="flex items-center gap-3 mt-10">
              <StepNumberCircle text="1" />
              <div className="text-neutral-900 text-[18px] font-normal">
                Select your data record lookup system (e.g. Shopify)
              </div>
            </div>

            {atLeastOneAutomationAvailable && (
              <div className="flex items-center gap-1 mt-1.5 ml-[40px]">
                <span className="text-neutral-900 text-[14px] font-normal">
                  This step is fully automated if you select a system marked with
                </span>
                <BoltIcon className="h-[15px] w-[15px] text-[#017F4D] bg-[#E2FDF3] p-0.5 rounded-3xl" />
              </div>
            )}

            <div className="ml-[40px] mt-1.5">
              <div>
                <div>
                  <Autocomplete
                    items={allVendors
                      .filter((vendor) => vendor.vendorId !== secondaryLookupSystemVendorId)
                      .map((vendor) => {
                        let showAutomationIcon: boolean = apiIntegrations?.vendors?.some(
                          (v) => v.vendorId === vendor.vendorId && v.lookupEnabled === true,
                        );
                        return {
                          id: vendor.vendorId,
                          name: vendor.name,
                          logoUrl: vendor.logoUrl,
                          trailingItemElem: showAutomationIcon ? (
                            <BoltIcon className="h-[20px] w-[20px] text-[#017F4D] bg-[#E2FDF3] p-0.5 rounded-3xl" />
                          ) : undefined,
                        } as AutocompleteItem;
                      })}
                    selectedItem={primaryLookupVendor}
                    onSelect={async (item) => {
                      setPrimaryLookupSystemVendorId(item.id);
                      await Api.organization.updateOrganization(organization.id, {
                        name: organization.name,
                        logoUrl: organization.logoUrl,
                        regulations: [],
                        primaryLookupSystemVendorId: item.id,
                      });
                      await refreshOrg();
                    }}
                    leadingElement={
                      primaryLookupVendor ? (
                        <div className="w-[50px] h-full flex items-center justify-center">
                          <img
                            src={primaryLookupVendor.logoUrl}
                            alt={primaryLookupVendor.name}
                            className="h-[32px] w-[32px]"
                          />
                        </div>
                      ) : (
                        <div className="w-[50px] h-full border border-solid border-neutral-400 rounded-l-sm flex items-center justify-center">
                          <MagnifyingGlassIcon className="w-[18px] h-[28px] text-neutral-900" />
                        </div>
                      )
                    }
                    // 66px = 50px (width of leading element) + 16px (padding on the left side of the input)
                    leadingInputPaddingPx={66}
                    trailingElement={
                      primaryLookupVendor ? (
                        <div className="p-3 flex items-center">
                          {isPrimaryVendorAutomationConnected && (
                            <div className="mr-[32px]">
                              <BoltIcon className="h-[24px] w-[24px] text-[#017F4D] bg-[#E2FDF3] p-0.5 rounded-3xl" />
                            </div>
                          )}
                          <Button
                            color="tertiary"
                            size="s"
                            onClick={async () => {
                              setPrimaryLookupSystemVendorId(null);
                              await Api.organization.updateOrganization(organization.id, {
                                name: organization.name,
                                logoUrl: organization.logoUrl,
                                regulations: [],
                                primaryLookupSystemVendorId: "_DELETE_VENDOR_ID",
                              });
                              await refreshOrg();
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      ) : undefined
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4">
              <StepNumberCircle text="2" />
              <div className="text-neutral-900 text-[18px] font-normal">
                (Optional) Set a secondary lookup system (e.g. Klaviyo)
              </div>
            </div>

            <div className="text-neutral-900 text-[14px] font-normal mt-1.5 ml-[40px]">
              This can be helpful, for example, if your main system does not contain all of your
              marketing contacts.
            </div>

            <div className="ml-[40px] mt-1.5">
              <div>
                <div>
                  <Autocomplete
                    items={allVendors
                      .filter((vendor) => vendor.vendorId !== primaryLookupSystemVendorId)
                      .map((vendor) => {
                        let showAutomationIcon: boolean = apiIntegrations?.vendors?.some(
                          (v) => v.vendorId === vendor.vendorId && v.lookupEnabled === true,
                        );
                        return {
                          id: vendor.vendorId,
                          name: vendor.name,
                          logoUrl: vendor.logoUrl,
                          trailingItemElem: showAutomationIcon ? (
                            <BoltIcon className="h-[20px] w-[20px] text-[#017F4D] bg-[#E2FDF3] p-0.5 rounded-3xl" />
                          ) : undefined,
                        } as AutocompleteItem;
                      })}
                    selectedItem={secondaryLookupVendor}
                    onSelect={async (item) => {
                      setSecondaryLookupSystemVendorId(item.id);
                      await Api.organization.updateOrganization(organization.id, {
                        name: organization.name,
                        logoUrl: organization.logoUrl,
                        regulations: [],
                        secondaryLookupSystemVendorId: item.id,
                      });
                      await refreshOrg();
                    }}
                    leadingElement={
                      secondaryLookupVendor ? (
                        <div className="w-[50px] h-full flex items-center justify-center">
                          <img
                            src={secondaryLookupVendor.logoUrl}
                            alt={secondaryLookupVendor.name}
                            className="h-[32px] w-[32px]"
                          />
                        </div>
                      ) : (
                        <div className="w-[50px] h-full border border-solid border-neutral-400 rounded-l-sm flex items-center justify-center">
                          <MagnifyingGlassIcon className="w-[18px] h-[28px] text-neutral-900" />
                        </div>
                      )
                    }
                    // 66px = 50px (width of leading element) + 16px (padding on the left side of the input)
                    leadingInputPaddingPx={66}
                    trailingElement={
                      secondaryLookupVendor ? (
                        <div className="p-3 flex items-center">
                          {isSecondaryVendorAutomationConnected && (
                            <div className="mr-[32px]">
                              <BoltIcon className="h-[24px] w-[24px] text-[#017F4D] bg-[#E2FDF3] p-0.5 rounded-3xl" />
                            </div>
                          )}
                          <Button
                            color="tertiary"
                            size="s"
                            onClick={async () => {
                              setSecondaryLookupSystemVendorId(null);
                              await Api.organization.updateOrganization(organization.id, {
                                name: organization.name,
                                logoUrl: organization.logoUrl,
                                regulations: [],
                                secondaryLookupSystemVendorId: "_DELETE_VENDOR_ID",
                              });
                              await refreshOrg();
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      ) : undefined
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4">
              <StepNumberCircle text="3" />
              <div className="text-neutral-900 text-[18px] font-normal">
                (Optional) Add additional notes for your team to lookup a consumer
              </div>
            </div>
            <div className="text-neutral-900 text-[14px] font-normal mt-1.5 ml-[40px]">
              Ex: "search internal database for consumer's name and email address"
            </div>
            <textarea
              rows={4}
              cols={62}
              className="ml-[40px] mt-1.5 block rounded-[5px] bg-white px-3 py-1.5 text-base text-neutral-800 border border-solid border-neutral-400 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
              defaultValue={instructions}
              onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
                let newInstructions = e.target.value;
                setInstructions(newInstructions);
                await Api.requestHandlingInstructions.updateConsumerGroupRequestHandlingInstructions(
                  {
                    organizationId: organization.id,
                    requestHandlingInstructions: newInstructions,
                  },
                );
              }}
            />
          </div>
        </div>
      </>
    </WorkflowSetupPageWrapper>
  );
};
